import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, 
  Settings, DollarSign, Globe, Key, 
  Save, X, AlertCircle, CheckCircle 
} from 'lucide-react';
import { bookingAPI } from '../services/api';
import toast from 'react-hot-toast';

const BookingAPIs = () => {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAPI, setEditingAPI] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    api_key: '',
    webhook_secret: '',
    webhook_url: '',
    commission_rate: 20.00,
    config: {}
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAPIs();
  }, []);

  const fetchAPIs = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getAll();
      setApis(response.data || []);
    } catch (error) {
      console.error('Error fetching APIs:', error);
      toast.error('Failed to fetch booking APIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const newErrors = {};
    const normalizedName = (formData.name || '').toLowerCase();
    if (!editingAPI) {
      if (!normalizedName) newErrors.name = 'API name is required';
      if (!/^[a-z0-9_-]+$/.test(normalizedName)) {
        newErrors.name = 'Lowercase letters, numbers, dash or underscore only';
      }
    }
    if (!formData.display_name) newErrors.display_name = 'Display name is required';
    const rateNum = parseFloat(formData.commission_rate);
    if (Number.isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      newErrors.commission_rate = 'Commission must be between 0 and 100';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the form errors');
      return;
    }

    try {
      if (editingAPI) {
        await bookingAPI.update(editingAPI.id, { ...formData, commission_rate: rateNum });
        toast.success('API updated successfully');
      } else {
        await bookingAPI.create({ ...formData, name: normalizedName, commission_rate: rateNum });
        toast.success('API created successfully');
      }
      
      setShowModal(false);
      setEditingAPI(null);
      resetForm();
      fetchAPIs();
    } catch (error) {
      console.error('Error saving API:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to save API');
    }
  };

  const handleEdit = (api) => {
    setEditingAPI(api);
    setFormData({
      name: api.name,
      display_name: api.display_name,
      api_key: api.api_key || '',
      webhook_secret: api.webhook_secret || '',
      webhook_url: api.webhook_url || '',
      commission_rate: api.commission_rate,
      config: api.config || {}
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this API? This action cannot be undone.')) {
      return;
    }

    try {
      await bookingAPI.delete(id);
      toast.success('API deleted successfully');
      fetchAPIs();
    } catch (error) {
      console.error('Error deleting API:', error);
      toast.error('Failed to delete API');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await bookingAPI.toggleActive(id);
      toast.success('API status updated');
      fetchAPIs();
    } catch (error) {
      console.error('Error toggling API status:', error);
      toast.error('Failed to update API status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      api_key: '',
      webhook_secret: '',
      webhook_url: '',
      commission_rate: 20.00,
      config: {}
    });
    setErrors({});
  };

  const openCreateModal = () => {
    setEditingAPI(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAPI(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading booking APIs...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking APIs</h1>
          <p className="text-gray-600">Manage multiple booking platforms and commission rates</p>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add New API
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Total APIs</p>
              <p className="text-xl font-bold text-gray-900">{apis.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Active</p>
              <p className="text-xl font-bold text-gray-900">
                {apis.filter(api => api.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <EyeOff className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Inactive</p>
              <p className="text-xl font-bold text-gray-900">
                {apis.filter(api => !api.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Avg Commission</p>
              <p className="text-xl font-bold text-gray-900">
                {apis.length > 0 
                  ? `${(apis.reduce((sum, api) => sum + parseFloat(api.commission_rate), 0) / apis.length).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* APIs List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Platform</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Commission</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Webhook</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apis.map((api) => (
                <tr key={api.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div>
                      <div className="font-medium text-gray-900">{api.display_name}</div>
                      <div className="text-sm text-gray-500">{api.name}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {api.commission_rate}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      api.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {api.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-gray-600">
                      {api.webhook_secret ? (
                        <span className="text-green-600">✓ Configured</span>
                      ) : (
                        <span className="text-gray-400">Not configured</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(api.id)}
                        className={`p-1 rounded ${
                          api.is_active 
                            ? 'text-orange-600 hover:bg-orange-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={api.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {api.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => handleEdit(api)}
                        className="p-1 rounded text-blue-600 hover:bg-blue-50"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(api.id)}
                        className="p-1 rounded text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAPI ? 'Edit Booking API' : 'Add New Booking API'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Name (Internal)
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-field"
                      placeholder="e.g., tripadvisor, viator"
                      required
                      disabled={!!editingAPI} // Can't change name after creation
                    />
                    {!editingAPI && errors.name && (
                      <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Internal identifier (lowercase, no spaces)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                      className="input-field"
                      placeholder="e.g., TripAdvisor, Viator"
                      required
                    />
                    {errors.display_name && (
                      <p className="mt-1 text-xs text-red-600">{errors.display_name}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <input
                      type="text"
                      value={formData.api_key}
                      onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                      className="input-field"
                      placeholder="API key from platform"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({...formData, commission_rate: e.target.value})}
                      className="input-field"
                      placeholder="20.00"
                      required
                    />
                    {errors.commission_rate && (
                      <p className="mt-1 text-xs text-red-600">{errors.commission_rate}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Secret
                  </label>
                  <input
                    type="text"
                    value={formData.webhook_secret}
                    onChange={(e) => setFormData({...formData, webhook_secret: e.target.value})}
                    className="input-field"
                    placeholder="Secret for webhook validation"
                  />
                  {!formData.webhook_secret && (
                    <p className="mt-1 text-xs text-amber-600">Optional but recommended for validating incoming webhooks.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                    className="input-field"
                    placeholder="https://yourdomain.com/api/webhooks/tripadvisor"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={
                      (!editingAPI && (!!errors.name || !formData.name)) ||
                      !!errors.display_name || !!errors.commission_rate || !formData.display_name
                    }
                  >
                    <Save className="w-4 h-4" />
                    {editingAPI ? 'Update API' : 'Create API'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingAPIs;
